import { useHandler } from "@redotech/react-util/hook";
import classNames from "classnames";
import { memo, useMemo } from "react";
import {
  Outlet,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { CartDrawer, MarketplaceFooter } from "../components";
import { useCart, useCartUI } from "../context";
import { MarketplaceNavbarShadcn } from "../marketplace/marketplace-navbar-shadcn";

// Routes that should not have padding (full-width content)
const FULL_WIDTH_ROUTES = ["/"];

function shouldHidePadding(pathname: string): boolean {
  return FULL_WIDTH_ROUTES.some((route) => {
    if (route === "/") {
      return pathname === route;
    }
    return pathname.startsWith(route);
  });
}

export const MarketplaceLayout = memo(function MarketplaceLayout() {
  const { totalItems, isLoading } = useCart();
  const { isDrawerOpen, openDrawer, closeDrawer } = useCartUI();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const hidePadding = useMemo(() => {
    return shouldHidePadding(location.pathname);
  }, [location.pathname]);

  const handleCartClick = useHandler(() => {
    openDrawer();
  });

  const handleCartDrawerClose = useHandler(() => {
    closeDrawer();
  });

  const handleCheckout = useHandler(() => {
    navigate("/checkout");
    closeDrawer();
  });

  const handleSearchChange = useHandler((value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set("q", value);
    } else {
      newParams.delete("q");
    }
    setSearchParams(newParams);
  });

  return (
    <div className="h-full bg-white flex flex-col overflow-hidden">
      <MarketplaceNavbarShadcn
        cartItemCount={totalItems}
        onCartClick={handleCartClick}
        onSearchChange={handleSearchChange}
        searchValue={searchParams.get("q") || ""}
      />
      <div className="flex flex-col flex-1 overflow-hidden">
        <main className="w-full flex-1 overflow-auto">
          <div
            className={classNames(
              "h-full w-full",
              !hidePadding && "px-4 xl:px-8",
            )}
          >
            <Outlet />
          </div>
        </main>
        <MarketplaceFooter />
      </div>
      <CartDrawer
        isLoading={isLoading}
        isOpen={isDrawerOpen}
        onCheckout={handleCheckout}
        onClose={handleCartDrawerClose}
      />
    </div>
  );
});
